import React from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  
  /* Ensure consistent styling in new tab */
  * {
    box-sizing: border-box;
  }
`;

const OverlayContainer = styled.div`
  background-color: ${theme.colors?.background || '#1a1a1a'};
  border-radius: ${theme.borderRadius?.lg || '12px'};
  padding: 0;
  border: 1px solid ${theme.colors?.border || 'rgba(114, 210, 255, 0.25)'};
  box-shadow: ${theme.shadows?.lg || '0 25px 50px -12px rgba(0, 0, 0, 0.25)'};
  font-family: ${theme.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif'};
  overflow: hidden;
  max-width: 900px;
  width: 85vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  background: ${theme.colors?.surface || '#2a2a2a'};
  padding: ${theme.spacing?.lg || '16px'} ${theme.spacing?.xl || '24px'};
  border-bottom: 1px solid ${theme.colors?.primary || '#3b82f6'};
  font-weight: ${theme.typography?.fontWeights?.bold || '700'};
  font-size: ${theme.typography?.fontSizes?.xl || '20px'};
  color: ${theme.colors?.text || '#ffffff'};
  letter-spacing: ${theme.typography?.letterSpacing?.normal || '0'};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: ${theme.spacing?.sm || '8px'};
  
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 0;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  
  @media (min-width: 768px) {
    position: relative;
  }
`;

const ModeIndicator = styled.div`
  font-size: ${theme.typography?.fontSizes?.sm || '14px'};
  font-weight: ${theme.typography?.fontWeights?.medium || '500'};
  color: ${theme.colors?.primary || '#3b82f6'};
  padding: ${theme.spacing?.xs || '4px'} ${theme.spacing?.sm || '8px'};
  background: ${theme.colors?.primary ? theme.colors.primary + '20' : '#3b82f620'};
  border-radius: ${theme.borderRadius?.md || '8px'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const HeaderTitle = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
`;

const NewTabButton = styled.button`
  background-color: ${theme.colors.primaryDark};
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: ${theme.transitions.normal};
  box-shadow: ${theme.shadows.sm};
  
  &:hover {
    background-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Content = styled.div`
  padding: ${theme.spacing?.lg || '16px'} ${theme.spacing?.xxl || '32px'};
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: auto;
  gap: ${theme.spacing?.xxl || '32px'};
  width: 100%;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing?.xl || '24px'};
    padding: ${theme.spacing?.lg || '16px'} ${theme.spacing?.xl || '24px'};
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing?.xxl || '32px'};
  min-width: 0;
`;

const ShortcutCategory = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing?.xs || '4px'};
`;

const CategoryHeader = styled.h3`
  color: ${theme.colors?.primary || '#3b82f6'};
  font-size: ${theme.typography?.fontSizes?.md || '16px'};
  font-weight: ${theme.typography?.fontWeights?.semiBold || '600'};
  margin: 0 0 ${theme.spacing?.xs || '4px'} 0;
  padding-bottom: ${theme.spacing?.xs || '4px'};
  border-bottom: 1px solid ${theme.colors?.primary ? theme.colors.primary + '40' : '#3b82f640'};
  min-height: 32px;
  display: flex;
  align-items: center;
`;

const ShortcutSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing?.xs || '4px'};
`;

const ShortcutRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing?.sm || '8px'} 0;
  font-size: ${theme.typography?.fontSizes?.sm || '14px'};
  color: ${props => props.isActive 
    ? (theme.colors?.text || '#ffffff') 
    : (theme.colors?.textSecondary || theme.colors?.text + '80' || '#ffffff80')
  };
  border-bottom: 1px solid ${theme.colors?.surfaceHover || '#3a3a3a'};
  gap: ${theme.spacing?.lg || '16px'};
  opacity: ${props => props.isActive ? 1 : 0.5};
  transition: opacity ${theme.transitions?.normal || '0.2s ease'};
  height: 36px;
  line-height: 1.2;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ShortcutCommand = styled.span`
  font-size: ${theme.typography?.fontSizes?.sm || '14px'};
  flex: 1;
  cursor: ${props => props.tooltip ? 'help' : 'default'};
  white-space: nowrap;
  min-width: 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  height: 100%;
`;

const ShortcutKeys = styled.span`
  display: flex;
  gap: ${theme.spacing?.xs || '4px'};
  font-size: ${theme.typography?.fontSizes?.xs || '12px'};
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;
  height: 100%;
  line-height: 1.2;
`;

const Key = styled.span`
  background: ${theme.colors?.surface || '#2a2a2a'};
  color: ${theme.colors?.text || '#ffffff'};
  padding: 0 6px;
  border-radius: ${theme.borderRadius?.sm || '4px'};
  border: 1px solid ${theme.colors?.border || '#4a4a4a'};
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Source Code Pro', monospace;
  font-weight: ${theme.typography?.fontWeights?.medium || '500'};
  min-width: 24px;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  box-sizing: border-box;
  font-size: ${theme.typography?.fontSizes?.xs || '12px'};
  flex-shrink: 0;
  line-height: 1;
  
  /* Adjust arrow symbols specifically */
  ${props => (props.children === '←' || props.children === '→') && `
    transform: translateY(-0.5px);
  `}
`;

const ShortcutsOverlay = ({ isVisible, onClose, onOpenInNewTab, currentMode }) => {
  if (!isVisible) return null;
  
  const generateNewTabContent = (shortcuts, mode) => {
    const isActive = (shortcut) => {
      if (!mode) return true;
      return shortcut.modes.includes('all') || shortcut.modes.includes(mode);
    };

    const renderShortcuts = (categoryData) => {
      if (!categoryData) return '';
      return `
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <h3 style="color: #3b82f6; font-size: 16px; font-weight: 600; margin: 0 0 4px 0; padding-bottom: 4px; border-bottom: 1px solid #3b82f640; min-height: 32px; display: flex; align-items: center;">
            ${categoryData.title}
          </h3>
          <div style="display: flex; flex-direction: column; gap: 4px;">
            ${categoryData.shortcuts.map(shortcut => {
              const active = isActive(shortcut);
              return `
                <div style="display: flex; align-items: center; justify-content: space-between; padding: 8px 0; font-size: 14px; color: ${active ? '#ffffff' : '#ffffff80'}; border-bottom: 1px solid #3a3a3a; gap: 16px; opacity: ${active ? 1 : 0.5}; height: 36px; line-height: 1.2;">
                  <span style="flex: 1; white-space: nowrap; min-width: 0; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; height: 100%;" title="${shortcut.tooltip || ''}">
                    ${shortcut.cmd}
                  </span>
                  <span style="display: flex; gap: 4px; font-size: 12px; align-items: center; flex-shrink: 0; white-space: nowrap; height: 100%;">
                    ${shortcut.keys.map(key => `
                      <span style="background: #2a2a2a; color: #ffffff; padding: 0 6px; border-radius: 4px; border: 1px solid #4a4a4a; font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Source Code Pro', monospace; font-weight: 500; min-width: 24px; text-align: center; display: inline-flex; align-items: center; justify-content: center; height: 20px; font-size: 12px; line-height: 1;">
                        ${key}
                      </span>
                    `).join('')}
                  </span>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    };

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Keyboard Shortcuts</title>
          <style>
            body {
              margin: 0;
              padding: 24px;
              background-color: #1a1a1a;
              color: #ffffff;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif;
              box-sizing: border-box;
            }
            * {
              box-sizing: border-box;
            }
          </style>
        </head>
        <body>
          <div style="max-width: 900px; margin: 0 auto;">
            <div style="background: #2a2a2a; padding: 24px; border-bottom: 1px solid #3b82f6; font-weight: 700; font-size: 20px; color: #ffffff; text-align: center; margin-bottom: 32px; border-radius: 12px 12px 0 0;">
              <h1 style="margin: 0; font-size: 20px;">Keyboard Shortcuts</h1>
              ${mode ? `<div style="font-size: 14px; font-weight: 500; color: #3b82f6; padding: 4px 8px; background: #3b82f620; border-radius: 8px; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 8px; display: inline-block;">Current Mode: ${mode}</div>` : ''}
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 32px; padding: 0 32px 32px; align-items: start;">
              <div style="display: flex; flex-direction: column; gap: 32px; min-width: 0;">
                ${renderShortcuts(shortcuts[0])}
                ${renderShortcuts(shortcuts[4])}
                ${renderShortcuts(shortcuts[2])}
              </div>
              <div style="display: flex; flex-direction: column; gap: 32px; min-width: 0;">
                ${renderShortcuts(shortcuts[1])}
                ${renderShortcuts(shortcuts[3])}
                ${renderShortcuts(shortcuts[5])}
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const handleOpenInNewTab = () => {
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      const content = generateNewTabContent(shortcutCategories, currentMode);
      newWindow.document.write(content);
      newWindow.document.close();
    }
    
    if (onOpenInNewTab) {
      onOpenInNewTab();
    }
  };

  const shortcutCategories = [
    {
      title: "Video Playback Controls",
      shortcuts: [
        { cmd: 'Play/Pause', keys: ['Space'], modes: ['all'] },
        { cmd: 'Frame by frame', keys: ['←', '→'], modes: ['all'] },
        { cmd: 'Skip 50 frames', keys: ['Shift+←', 'Shift+→'], modes: ['all'] },
      ]
    },
    {
      title: "General",
      shortcuts: [
        { cmd: 'Add annotation (other modes)', keys: ['Left-click'], modes: ['bounce', 'net', 'misc'] },
        { cmd: 'Switch mode', keys: ['M'], modes: ['all'] },
        { cmd: 'Remove annotation', keys: ['X'], modes: ['all'] },
        { cmd: 'Save annotations', keys: ['S'], tooltip: 'Re-exports an uploaded strokes file in its original structure; otherwise saves the all-annotations format', modes: ['all'] },
        { cmd: 'Save all-annotations format', keys: ['Shift+S'], tooltip: 'Force the all-annotations format, the only one carrying court keypoints, player boxes and strokes added in the app', modes: ['all'] },
        { cmd: 'Cycle custom label', keys: ['G'], tooltip: 'Cycle through custom labels (none → label1 → label2 → ...)', modes: ['all'] },
        { cmd: 'Toggle flag for review', keys: ['R'], tooltip: 'Mark/unmark annotation as flagged for review', modes: ['all'] },
        { cmd: 'Call stroke in / out', keys: ['I', 'O'], tooltip: 'Set the call for the stroke shown in the badge, pin its line confidence to 0.9 and keep the original values', modes: ['all'] },
      ]
    },
    {
      title: "Ball Tracking",
      shortcuts: [
        { cmd: 'Add stroke (bottom/top player)', keys: ['Left-click', 'Right-click'], tooltip: 'Right-click auto-infers stroke type from player position and ball location', modes: ['stroke'] },
        { cmd: 'Set stroke type (FH/BH/S/SM)', keys: ['H', 'J', 'K', 'L'], tooltip: 'H=forehand, J=backhand, K=serve, L=smash', modes: ['stroke'] },
        { cmd: 'Set player position (top/bottom)', keys: ['Y', 'U'], modes: ['stroke'] },
        { cmd: 'Label ball not in frame', keys: ['N'], tooltip: 'Mark current frame as ball not visible (saved as -9999,-9999 for negative training)', modes: ['stroke', 'bounce', 'net', 'misc'] },
      ]
    },
    {
      title: "Player Tracking",
      shortcuts: [
        { cmd: 'Add player bounding box (top/bottom)', keys: ['B', 'T'], modes: ['player'] },
        { cmd: 'Remove player bounding box (top/bottom)', keys: ['Shift+B', 'Shift+T'], modes: ['player'] },
        { cmd: 'Toggle out-of-frame', keys: ['Right-click'], modes: ['player'] },
      ]
    },
    {
      title: "Court Detection",
      shortcuts: [
        { cmd: 'Predict court', keys: ['C'], modes: ['all'] },
        { cmd: 'Copy previous court', keys: ['Ctrl+C'], modes: ['court'] },
      ]
    },
    {
      title: "Rally Scoring",
      shortcuts: [
        { cmd: 'Start / end rally at playhead', keys: ['Enter'], tooltip: 'First press marks the rally start, the next marks its end', modes: ['rally'] },
        { cmd: 'Winner: near / far player', keys: ['1', '2'], tooltip: 'Scores the rally that was just ended', modes: ['rally'] },
        { cmd: 'Cancel rally in progress', keys: ['Esc'], tooltip: 'Discards the pending start; press again to leave rally mode', modes: ['rally'] },
        { cmd: 'Save rallies JSON', keys: ['S'], modes: ['rally'] },
      ]
    }
  ];

  // Helper function to check if shortcut is active in current mode
  const isShortcutActive = (shortcut) => {
    if (!currentMode) return true; // Show all if no mode provided
    return shortcut.modes.includes('all') || shortcut.modes.includes(currentMode);
  };

  // Two stacked columns rather than a row-major flow: Video Playback Controls is short, so
  // letting Court Detection sit directly beneath it fills what was otherwise dead space.
  const gridColumns = [
    [
      shortcutCategories[0], // Video Playback Controls
      shortcutCategories[4], // Court Detection
      shortcutCategories[2], // Ball Tracking
    ],
    [
      shortcutCategories[1], // General
      shortcutCategories[3], // Player Tracking
      shortcutCategories[5], // Rally Scoring
    ],
  ];

  const renderCategory = (category, index) => {
    if (!category) return <div key={index}></div>; // Empty placeholder
    
    return (
      <ShortcutCategory key={index}>
        <CategoryHeader>{category.title}</CategoryHeader>
        <ShortcutSection>
          {category.shortcuts.map((shortcut, shortcutIndex) => {
            const isActive = isShortcutActive(shortcut);
            return (
              <ShortcutRow key={shortcutIndex} isActive={isActive}>
                <ShortcutCommand title={shortcut.tooltip || ''} tooltip={!!shortcut.tooltip}>
                  {shortcut.cmd}
                </ShortcutCommand>
                <ShortcutKeys>
                  {shortcut.keys.map((key, keyIndex) => (
                    <Key key={keyIndex}>{key}</Key>
                  ))}
                </ShortcutKeys>
              </ShortcutRow>
            );
          })}
        </ShortcutSection>
      </ShortcutCategory>
    );
  };

  return (
    <Backdrop onClick={onClose}>
      <OverlayContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderTop>
            <HeaderTitle>Keyboard Shortcuts</HeaderTitle>
            <NewTabButton onClick={handleOpenInNewTab}>
              Open in new tab
            </NewTabButton>
          </HeaderTop>
          {currentMode && (
            <ModeIndicator>
              Current Mode: {currentMode}
            </ModeIndicator>
          )}
        </Header>
        <Content>
          {gridColumns.map((column, columnIndex) => (
            <Column key={columnIndex}>
              {column.map((category, index) => renderCategory(category, `${columnIndex}-${index}`))}
            </Column>
          ))}
        </Content>
      </OverlayContainer>
    </Backdrop>
  );
};

export default ShortcutsOverlay;
